import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, Search, X } from 'lucide-react';
import axios from 'axios';
import { API_URL } from '../../services/api';
import '../AllCss/StoreModal.css';

const DAY_NAMES_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DAY_NAMES_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** Parse "8 AM - 7 PM" or "Closed" into minutes or null. */
function parseTimeRange(str) {
  if (!str || typeof str !== 'string') return null;
  const trimmed = str.trim();
  if (/closed/i.test(trimmed)) return null;
  const match = trimmed.match(/^(\d{1,2})\s*(AM|PM)\s*[-–—]\s*(\d{1,2})\s*(AM|PM)$/i);
  if (!match) return null;
  const parseMinutes = (hourStr, ampm) => {
    const h = parseInt(hourStr, 10);
    if (ampm.toUpperCase() === 'PM' && h !== 12) return (h + 12) * 60;
    if (ampm.toUpperCase() === 'AM' && h === 12) return 0;
    return h * 60;
  };
  return { start: parseMinutes(match[1], match[2]), end: parseMinutes(match[3], match[4]) };
}

/** Parse operatingHours string into per-day hours (Sun=0 .. Sat=6). */
function parseOperatingHours(operatingHours) {
  const dayHours = Array(7).fill(null).map(() => ({ hoursText: 'Closed' }));
  if (!operatingHours || typeof operatingHours !== 'string' || !operatingHours.trim()) {
    return dayHours;
  }
  const segments = operatingHours.split(/\s*;\s*/).map((s) => s.trim());
  for (let d = 0; d < 7; d++) {
    const dayName = DAY_NAMES_FULL[d];
    const seg = segments.find((s) => s.startsWith(`${dayName}:`));
    if (!seg) continue;
    const afterColon = seg.slice(dayName.length + 1).trim();
    dayHours[d] = { hoursText: afterColon || 'Closed' };
  }
  return dayHours;
}

function buildScheduleLines(dayHours) {
  const lines = [];
  let i = 0;
  while (i < 7) {
    const text = dayHours[i].hoursText;
    let j = i + 1;
    while (j < 7 && dayHours[j].hoursText === text) j++;
    const dayLabel = j - i === 1 ? DAY_NAMES_SHORT[i] : `${DAY_NAMES_SHORT[i]}-${DAY_NAMES_SHORT[j - 1]}`;
    lines.push(`${dayLabel}: ${text}`);
    i = j;
  }
  return lines;
}

function getStoreHoursInfo(operatingHours) {
  const dayHours = parseOperatingHours(operatingHours);
  const now = new Date();
  const currentDay = now.getDay();
  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  const today = dayHours[currentDay];
  const todayHoursString = today ? today.hoursText : 'Closed';
  const range = parseTimeRange(todayHoursString);
  const isOpen = range && currentMinutes >= range.start && currentMinutes <= range.end;
  const scheduleLines = buildScheduleLines(dayHours);
  return { isOpen, todayHoursString, scheduleLines };
}

function StoreHours({ operatingHours }) {
  const [expanded, setExpanded] = useState(false);
  const { isOpen, todayHoursString, scheduleLines } = getStoreHoursInfo(operatingHours);

  return (
    <div className="store-modal-hours-block">
      <button
        type="button"
        className="store-modal-hours-row"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        aria-label={expanded ? 'Hide full schedule' : 'Show full schedule'}
      >
        <span className={`store-modal-status ${isOpen ? 'store-modal-status-open' : 'store-modal-status-closed'}`}>
          {isOpen ? 'Currently open' : 'Currently closed'}
        </span>
        <span className="store-modal-today-hours">{todayHoursString}</span>
        <ChevronDown size={18} className={`store-modal-hours-chevron ${expanded ? 'store-modal-hours-chevron-open' : ''}`} aria-hidden />
      </button>
      {expanded && (
        <div className="store-modal-schedule">
          {scheduleLines.map((line, idx) => (
            <div key={idx} className="store-modal-schedule-line">
              {line}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function StoreModal({ isOpen, onClose, currentZip, onLocationChange }) {
  const navigate = useNavigate();
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(false);
  const [changeInput, setChangeInput] = useState('');
  const [searching, setSearching] = useState(false);
  const [changeFocused, setChangeFocused] = useState(false);

  const fetchStore = async (zipOrState) => {
    if (!zipOrState || !String(zipOrState).trim()) return;
    setLoading(true);
    try {
      const trimmed = String(zipOrState).trim();
      const isState = /^[A-Za-z]{2}$/.test(trimmed);
      const params = isState ? { state: trimmed.toUpperCase() } : { zip: trimmed };
      const { data } = await axios.get(`${API_URL}/api/shipping/store-by-zip`, {
        withCredentials: true, params });
      if (data.success && data.data?.store) {
        setStore(data.data.store);
      } else if (data.success && data.data?.stores?.length) {
        setStore(data.data.stores[0]);
      } else {
        setStore(null);
      }
    } catch {
      setStore(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && currentZip) {
      fetchStore(currentZip);
    }
  }, [isOpen, currentZip]);

  useEffect(() => {
    if (!isOpen) {
      setChangeInput('');
      setChangeFocused(false);
    }
  }, [isOpen]);

  const handleChangeStore = async (e) => {
    e.preventDefault();
    const val = changeInput.trim();
    if (!val) return;
    setSearching(true);
    try {
      const isState = /^[A-Za-z]{2}$/.test(val);
      const params = isState ? { state: val.toUpperCase() } : { zip: val.replace(/\D/g, '').slice(0, 5) };
      const { data } = await axios.get(`${API_URL}/api/shipping/store-by-zip`, {
        withCredentials: true, params });
      const newStore = data?.data?.store || data?.data?.stores?.[0];
      if (newStore) {
        setStore(newStore);
        onLocationChange(newStore.zipCode || val);
        setChangeInput('');
      } else {
        setStore(null);
      }
    } catch {
      setStore(null);
    } finally {
      setSearching(false);
    }
  };

  const handleShopCars = () => {
    onClose();
    navigate('/inventory');
  };

  if (!isOpen) return null;

  const cityLine = store
    ? [store.city, store.state, store.zipCode].filter(Boolean).join(', ')
    : '';

  return (
    <div
      className="store-modal-backdrop"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="store-modal-zip-label"
    >
      <div className="store-modal" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="store-modal-close" onClick={onClose} aria-label="Close">
          <X size={20} />
        </button>

        <div className="store-modal-primary">
          {loading ? (
            <div className="store-modal-loading">Loading store…</div>
          ) : store ? (
            <>
              <p id="store-modal-zip-label" className="store-modal-zip-label">
                Your store for {currentZip || '—'}:
              </p>
              <h2 className="store-modal-store-name">{store.name}</h2>
              <a href="/contact" className="store-modal-details-link" onClick={onClose}>
                See all store details
              </a>
              {(store.address || cityLine) && (
                <address className="store-modal-address">
                  {store.address && <span className="store-modal-address-line">{store.address}</span>}
                  {cityLine && <span className="store-modal-address-line">{cityLine}</span>}
                </address>
              )}
              {store.phone && (
                <p className="store-modal-phone">
                  <a href={`tel:${store.phone.replace(/\D/g, '')}`}>{store.phone}</a>
                </p>
              )}
              {store.operatingHours && <StoreHours operatingHours={store.operatingHours} />}
              <button type="button" className="store-modal-shop-btn" onClick={handleShopCars}>
                Shop cars at this store
              </button>
            </>
          ) : (
            <>
              <p className="store-modal-zip-label">Your store for {currentZip || '—'}:</p>
              <p className="store-modal-no-store">No store found for this zip. Try another zip or state below.</p>
            </>
          )}
        </div>

        <div className="store-modal-change">
          <h3 className="store-modal-change-title">Change your store</h3>
          <form onSubmit={handleChangeStore} className="store-modal-change-form">
            <div
              className={`store-modal-change-field ${changeFocused || changeInput ? 'store-modal-change-field-active' : ''}`}
            >
              <label htmlFor="store-modal-change-input" className="store-modal-change-label">
                Enter zip or state
              </label>
              <input
                id="store-modal-change-input"
                type="text"
                className="store-modal-change-input"
                value={changeInput}
                onChange={(e) => setChangeInput(e.target.value)}
                onFocus={() => setChangeFocused(true)}
                onBlur={() => setChangeFocused(false)}
                autoComplete="postal-code"
              />
              <button
                type="submit"
                className="store-modal-change-search"
                disabled={searching || !changeInput.trim()}
                aria-label="Search for store"
              >
                <Search size={20} strokeWidth={2} />
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
